import {
  CalendarRange,
  FileKey,
  Flag,
  Layers3,
  MapPin,
  Plus,
  Route,
  Tags,
  TrafficCone,
} from "lucide-react";

import { formatPossessionRange } from "../../components/PossessionNavigator";

const errorMessages = (value) => {
  if (!value) return [];
  return (Array.isArray(value) ? value : [value]).filter(Boolean);
};

export const countErrorsForPath = (errors, path) => Object.entries(errors || {}).reduce(
  (total, [key, value]) => total + (key === path || key.startsWith(`${path}.`) ? errorMessages(value).length : 0),
  0,
);

const hasValue = (value) => value !== undefined && value !== null && value !== "";
const indexedLabel = (value, fallback) => hasValue(value) ? String(value) : fallback;

const leaf = ({ complete, description, errorPath, errors, hideIcon = false, icon, id, label, targetId }) => ({
  complete,
  description,
  errorCount: countErrorsForPath(errors, errorPath),
  hideIcon,
  icon,
  id,
  label,
  targetId,
});

export const buildLocationFieldTree = ({ errors = {}, nestedErrorCounts = {}, onAddPossession, values = {} }) => {
  const location = values.location || {};
  const possessions = values.possessions || [];
  const possessionErrors = errorMessages(errors["data.possessions"]);
  const identificationChildren = [
    leaf({ complete: Boolean(location.project), errorPath: "data.location.project", errors, hideIcon: true, id: "location-project", label: "Project", targetId: "location--project" }),
    leaf({ complete: hasValue(location.won_item_no), errorPath: "data.location.won_item_no", errors, hideIcon: true, id: "location-won", label: "WON item number", targetId: "location--won_item_no" }),
    leaf({ complete: hasValue(location.possession_no), errorPath: "data.location.possession_no", errors, hideIcon: true, id: "location-reference", label: "Possession number", targetId: "location--possession_no" }),
    leaf({ complete: hasValue(location.week_no), errorPath: "data.location.week_no", errors, hideIcon: true, id: "location-week", label: "Week number", targetId: "location--week_no" }),
    leaf({ complete: hasValue(values.conversion_unit), errorPath: "data.conversion_unit", errors, hideIcon: true, id: "location-distance-unit", label: "Distance measurement", targetId: "location--conversion_unit" }),
    leaf({ complete: true, errorPath: "data.location.is_multi_possession", errors, hideIcon: true, id: "location-multi-possession", label: "Multiple part possession", targetId: "location--is_multi_possession" }),
  ];

  const possessionChildren = possessions.map((possession, possessionIndex) => {
    const possessionPath = `data.possessions.${possessionIndex}`;
    const worksiteChildren = (possession.worksites || []).map((worksite, worksiteIndex) => {
      const worksitePath = `${possessionPath}.worksites.${worksiteIndex}`;
      const lineChildren = (worksite.lines_at_site || []).map((line, lineIndex) => {
        const linePath = `${worksitePath}.lines_at_site.${lineIndex}`;
        return {
          complete: hasValue(line.name) && hasValue(line.from_elr?.elr) && hasValue(line.to_elr?.elr),
          description: [line.from_elr?.elr, line.to_elr?.elr].filter(Boolean).join(" to ") || "Line details incomplete",
          errorCount: nestedErrorCounts[`line-${possessionIndex}-${worksiteIndex}-${lineIndex}`] ?? countErrorsForPath(errors, linePath),
          icon: Route,
          id: `location-possession-${possessionIndex}-worksite-${worksiteIndex}-line-${lineIndex}`,
          label: indexedLabel(line.name, `Line ${lineIndex + 1}`),
          possessionIndex,
          targetId: `line-from-elr-${possessionIndex}-${worksiteIndex}-${lineIndex}`,
        };
      });
      return {
        children: lineChildren,
        complete: hasValue(worksite.worksite_ref) && lineChildren.length > 0 && lineChildren.every((line) => line.complete),
        defaultExpanded: countErrorsForPath(errors, worksitePath) > 0,
        description: `${lineChildren.length} ${lineChildren.length === 1 ? "line" : "lines"}`,
        errorCount: nestedErrorCounts[`worksite-${possessionIndex}-${worksiteIndex}`] ?? countErrorsForPath(errors, `${worksitePath}.worksite_ref`),
        icon: MapPin,
        id: `location-possession-${possessionIndex}-worksite-${worksiteIndex}`,
        label: indexedLabel(worksite.worksite_ref, `Worksite ${worksiteIndex + 1}`),
        possessionIndex,
        targetId: `worksite-ref-${possessionIndex}-${worksiteIndex}`,
      };
    });

    const relatedChildren = [
      ...(possession.level_crossings || []).map((crossing, index) => ({
        complete: hasValue(crossing.level_crossing),
        errorCount: countErrorsForPath(errors, `${possessionPath}.level_crossings.${index}`),
        icon: TrafficCone,
        id: `location-possession-${possessionIndex}-crossing-${index}`,
        label: indexedLabel(crossing.level_crossing, `Level crossing ${index + 1}`),
        possessionIndex,
        targetId: `level-crossing-name-${possessionIndex}-${index}`,
      })),
      ...(possession.marker_boards || []).map((board, index) => ({
        complete: hasValue(board.name) && hasValue(board.location),
        errorCount: countErrorsForPath(errors, `${possessionPath}.marker_boards.${index}`),
        icon: Flag,
        id: `location-possession-${possessionIndex}-marker-${index}`,
        label: indexedLabel(board.name, `Marker board ${index + 1}`),
        possessionIndex,
        targetId: `marker-board-name-${possessionIndex}-${index}`,
      })),
    ];

    return {
      children: [...worksiteChildren, ...relatedChildren],
      complete: hasValue(possession.planned_start_date) && hasValue(possession.planned_end_date) && worksiteChildren.every((worksite) => worksite.complete),
      defaultExpanded: possessionIndex === 0 || countErrorsForPath(errors, possessionPath) > 0,
      description: formatPossessionRange(possession.planned_start_date, possession.planned_end_date),
      errorCount: nestedErrorCounts[`possession-${possessionIndex}`] ?? (
        countErrorsForPath(errors, `${possessionPath}.planned_start_date`) +
        countErrorsForPath(errors, `${possessionPath}.planned_end_date`)
      ),
      icon: CalendarRange,
      id: `location-possession-${possessionIndex}`,
      label: `Part ${possessionIndex + 1}`,
      possessionIndex,
      targetId: `possession-${possessionIndex + 1}-period-start`,
    };
  });

  return [
    {
      children: identificationChildren,
      complete: identificationChildren.filter((item) => item.id !== "location-project").every((item) => item.complete),
      defaultExpanded: true,
      errorCount: 0,
      icon: FileKey,
      id: "location-identification",
      label: "Pack identification",
      targetId: "location--project",
    },
    {
      action: location.is_multi_possession === 1 && onAddPossession ? {
        icon: Plus,
        label: "Add part",
        onClick: onAddPossession,
      } : undefined,
      children: possessionChildren,
      complete: possessionChildren.length > 0 && possessionChildren.every((item) => item.complete),
      defaultExpanded: true,
      description: possessionErrors[0] || `${possessionChildren.length} ${possessionChildren.length === 1 ? "part" : "parts"}`,
      errorCount: possessionErrors.filter((message) => !/Part\s+\d+/i.test(String(message))).length,
      icon: Layers3,
      id: "location-possessions",
      label: "Possession",
      targetId: "possessions-heading",
    },
    {
      complete: [1, 2, 3, 4, 5, 6].some((number) => hasValue(location[`keyword_${number}`])),
      errorCount: [1, 2, 3, 4, 5, 6].reduce((total, number) => total + countErrorsForPath(errors, `data.location.keyword_${number}`), 0),
      icon: Tags,
      id: "location-keywords",
      label: "Keywords",
      targetId: "location--keyword_1",
    },
  ];
};
